import { useState } from "react";
import { Plus, Pencil, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListShell } from "@/components/shared/ListShell";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { ScreenGuard } from "@/components/rbac/ScreenGuard";
import { formatDate } from "@/utils/format";
import { useDeactivateStaff, useStaff, useUpdateStaff } from "@/hooks/useRbac";
import type { StaffMember } from "@/rbac/types";
import { StaffEditDialog } from "./StaffEditDialog";

export const StaffList = () => {
  const { data: rows = [], isLoading, error, refetch } = useStaff();
  const updateStaff = useUpdateStaff();
  const deactivate = useDeactivateStaff();
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [creating, setCreating] = useState(false);

  const toggleActive = (row: StaffMember) =>
    updateStaff.mutate(
      { id: row.id, data: { isActive: !row.isActive } },
      {
        onSuccess: () =>
          toast.success(
            row.isActive ? "Staff deactivated" : "Staff reactivated",
          ),
        onError: (e) => toast.error(e.message),
      },
    );

  const handleDeactivate = (row: StaffMember) => {
    if (
      !window.confirm(
        `Deactivate ${row.name}? They will lose access immediately.`,
      )
    )
      return;
    deactivate.mutate(row.id, {
      onSuccess: () => toast.success("Staff deactivated"),
      onError: (e) => toast.error(e.message),
    });
  };

  const columns: Array<DataTableColumn<StaffMember>> = [
    {
      key: "name",
      header: "Name",
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    {
      key: "email",
      header: "Email",
      render: (r) => <span className="text-muted-foreground">{r.email}</span>,
    },
    {
      key: "role",
      header: "Role",
      render: (r) =>
        r.role ? (
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            {r.role.name}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">No role</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <StatusBadge status={r.isActive ? "active" : "disabled"} />
      ),
    },
    {
      key: "lastLogin",
      header: "Last login",
      render: (r) => (
        <span className="text-muted-foreground">
          {r.lastLoginAt ? formatDate(r.lastLoginAt) : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <PermissionGate module="staffManagement" action="edit">
            <button
              type="button"
              onClick={() => setEditing(r)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground"
              aria-label="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </PermissionGate>
          <PermissionGate module="staffManagement" action="edit">
            <button
              type="button"
              onClick={() => toggleActive(r)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground"
              aria-label={r.isActive ? "Deactivate" : "Reactivate"}
            >
              {r.isActive ? (
                <UserX className="h-4 w-4" />
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
            </button>
          </PermissionGate>
          <PermissionGate module="staffManagement" action="delete">
            <button
              type="button"
              onClick={() => handleDeactivate(r)}
              className="p-1.5 rounded hover:bg-destructive/10 text-destructive"
              aria-label="Deactivate"
            >
              <UserX className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <ScreenGuard module="staffManagement">
      <AppLayout title="Staff">
        <ListToolbar
          actions={
            <PermissionGate module="staffManagement" action="create">
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium rounded-btn px-4 py-2 hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" /> Add staff
              </button>
            </PermissionGate>
          }
        />

        <ListShell
          isLoading={isLoading}
          error={error ? (error as Error).message : null}
          isEmpty={rows.length === 0}
          onRetry={refetch}
          emptyTitle="No staff yet"
          emptyDescription="Invite teammates and assign them a role."
        >
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} />
        </ListShell>

        {(editing || creating) && (
          <StaffEditDialog
            staff={editing}
            onClose={() => {
              setEditing(null);
              setCreating(false);
            }}
          />
        )}
      </AppLayout>
    </ScreenGuard>
  );
};

export default StaffList;
